/* global DetailsModal, CompareUtil, trapFocus, removeTrapFocus */

customElements.whenDefined('details-modal').then(() => {
  class CompareModal extends DetailsModal {
    constructor() {
      super();
      this.compareContainer = this.querySelector('.compare-container');
      this.compareArea = this.compareContainer.querySelector('.js-compare-area');
      this.loadingSpinner = this.compareContainer.querySelector('.loading-spinner');
      this.closeButton = this.querySelectorAll('.js-compare-close');
      this.detailsEl.inert = !this.detailsEl.open;

      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.clickHandler);
      document.removeEventListener('keydown', this.keydownHandler);
      this.detailsEl.removeEventListener('keydown', this.focusTrapKeydownHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:load', CompareModal.handleSectionChange);
        document.removeEventListener('on:product-compare:reload', this.compareReloadHandler);
      }
    }

    bindEvents() {
      this.addEventListener('on:product-compare-modal:open', this.open);

      this.clickHandler = this.handleClick.bind(this);
      this.keydownHandler = this.handleKeydown.bind(this);
      this.focusTrapKeydownHandler = this.handleFocusTrapKeydown.bind(this);
      document.addEventListener('click', this.clickHandler);
      document.addEventListener('keydown', this.keydownHandler);
      this.detailsEl.addEventListener('keydown', this.focusTrapKeydownHandler);

      this.closeButton.forEach((closeButton) => {
        closeButton.addEventListener('click', this.close.bind(this));
      });

      // Refresh the compare when the Compare section get updated
      if (Shopify.designMode) {
        this.compareReloadHandler = this.renderCompareTable.bind(this);
        document.addEventListener('shopify:section:load', CompareModal.handleSectionChange);
        document.addEventListener('on:product-compare:reload', this.compareReloadHandler);
      }
    }

    /**
     * Handles a 'click' event for remove from compare
     * @param {object} evt - Event object
     */
    handleClick(evt) {
      if (evt.target.classList.contains('js-compare-col-remove')) {
        evt.preventDefault();

        CompareUtil.removeFromCompare(evt.target.dataset.productId.toString(), true);

        const compareCount = CompareUtil.getCompareCount();
        this.compareContainer.dataset.compareCount = compareCount;
        if (compareCount === 1) {
          this.close();
        }
      }
    }

    /**
     * Closes the direct-launch comparison when focus remains on the external launcher.
     * The base DetailsModal Escape listener only receives events originating inside
     * its <details> element.
     * @param {KeyboardEvent} evt - Keyboard event.
     */
    handleKeydown(evt) {
      if (evt.key === 'Escape' && this.detailsEl.open) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Keeps focus inside the compare dialog even while asynchronous content changes
     * the global focus-trap endpoints.
     * @param {KeyboardEvent} evt - Keyboard event.
     */
    handleFocusTrapKeydown(evt) {
      if (evt.key !== 'Tab' || !this.detailsEl.open) return;
      const focusable = [...this.detailsEl.querySelectorAll(
        'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])'
      )].filter((element) => element.offsetParent && getComputedStyle(element).visibility !== 'hidden');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (evt.shiftKey && (evt.target === first || evt.target === this.detailsEl)) {
        evt.preventDefault();
        evt.stopPropagation();
        last.focus();
      } else if (!evt.shiftKey && evt.target === last) {
        evt.preventDefault();
        evt.stopPropagation();
        first.focus();
      }
    }

    /**
     * Opens the compare modal
     */
    async open() {
      this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.detailsEl.inert = false;
      this.modal.classList.remove('hidden');
      super.open();
      trapFocus(this.detailsEl, this.closeButton[0]);
      await this.renderCompareTable();
      if (this.detailsEl.open) trapFocus(this.detailsEl, this.closeButton[0]);
    }

    /**
     * Closes the compare modal and restores focus to its actual opener.
     */
    close() {
      const opener = this.opener;
      super.close();
      this.detailsEl.inert = true;
      if (opener?.isConnected) removeTrapFocus(opener);
      this.opener = null;
    }

    /**
     * Hook into the super function to hide the modal when it's finished closing
     * @param {object} evt - Event object
     */
    handleTransitionEnd(evt) {
      super.handleTransitionEnd(evt);
      if (this.modal.open === false) {
        this.modal.classList.add('hidden');
      }
    }

    /**
     * Triggers a refresh the compare table based on Theme Editor events
     * @param {object} evt - Event object
     */
    static handleSectionChange(evt) {
      if (evt.target.matches('.cc-compare')) {
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('on:product-compare:reload'));
        }, 1000);
      }
    }

    /**
     * Renders the compare table
     */
    async renderCompareTable() {
      // Reset the previous state if applicable
      this.loadingSpinner.removeAttribute('hidden');
      this.loadingSpinner.removeAttribute('aria-hidden');
      this.compareArea.classList.remove('revealable--in');
      this.compareContainer.querySelector('.js-compare-load-note')?.remove();
      this.compareArea.innerHTML = '';

      // Get the compare products
      const compareProducts = CompareUtil.getSelectedProducts();
      if (compareProducts) {
        // Transform the independently loaded product sections into one rectangular table.
        const compareResult = await CompareModal.getCompareProducts(compareProducts);
        if (compareResult.failedProducts.length) {
          CompareUtil.setSelectedProducts(compareResult.validProducts);
          const notice = document.createElement('p');
          notice.className = 'compare-prompt text-sm js-compare-load-note';
          notice.setAttribute('role', 'status');
          notice.textContent = compareResult.failedProducts.length === 1
            ? 'Ein nicht mehr verfügbares Produkt wurde aus dem Vergleich entfernt.'
            : 'Nicht mehr verfügbare Produkte wurden aus dem Vergleich entfernt.';
          this.compareArea.before(notice);
        }
        const validProducts = compareResult.validProducts;
        this.compareContainer.dataset.compareCount = validProducts.length;
        const compareFields = new Map();

        if (validProducts.length >= 2) {
          compareResult.items.forEach(({ product, html }) => {
            const responseHTML = document.implementation.createHTMLDocument();
            responseHTML.documentElement.innerHTML = html; // eslint-disable-line

            responseHTML.querySelectorAll('.shopify-section > div').forEach((compareField) => {
              const key = compareField.dataset.compareKey;
              if (!key) return;
              if (!compareFields.has(key)) compareFields.set(key, new Map());
              compareFields.get(key).set(product.id, compareField);
            });
          });

          // Create the compare flex
          const compareElem = document.createElement('div');
          compareElem.className = 'compare-temp';

          const keys = [...compareFields.keys()];
          const actionIndex = keys.indexOf('compare-actions');
          if (actionIndex >= 0) keys.push(keys.splice(actionIndex, 1)[0]);
          const hasHairOneFacts = keys.some((key) => key.startsWith('compare-hairone-'));
          const coreKeys = new Set(['compare-image', 'compare-title', 'compare-price', 'compare-actions']);
          this.compareArea.setAttribute('role', 'table');
          this.compareArea.setAttribute('aria-label', 'Produktvergleich');

          keys.forEach((key) => {
            const isHairOneFact = key.startsWith('compare-hairone-');
            const isRequiredHairWaxFinish = key === 'compare-hairone-finish'
              && validProducts.every((product) => product.group === 'hair-waxes');
            if (hasHairOneFacts && !coreKeys.has(key) && !isHairOneFact) return;
            if (key === 'compare-hairone-group' || key === 'compare-hairone-model') return;
            const fieldMap = compareFields.get(key);
            const value = validProducts.map((product) => {
              const existing = fieldMap.get(product.id);
              if (existing) return existing;
              const placeholder = document.createElement('div');
              placeholder.dataset.compareKey = key;
              placeholder.dataset.isEmptyMetafield = 'true';
              placeholder.className = `compare-col compare-col--${product.id}`;
              placeholder.innerHTML = '<div class="compare-value">—</div>';
              return placeholder;
            });

            // If we can show empty metafield rows, or not all product values are empty.
            if (isRequiredHairWaxFinish
              || theme.settings.compareShowEmptyMetafields
              || !value.every((elem) => !!elem.dataset.isEmptyMetafield)) {
              const compareRow = document.createElement('div');
              compareRow.className = `compare-row compare-row--${
                key.includes('metafield') ? 'metafield' : key.replace('compare-', '')
              }`;
              compareRow.setAttribute('role', 'row');
              compareRow.dataset.compareKey = key;
              compareRow.dataset.compareRowKey = key;
              const rowLabels = {
                'compare-image': 'Produktbild',
                'compare-title': 'Produkt',
                'compare-price': 'Preis',
                'compare-actions': 'Aktionen'
              };
              const visibleLabel = value[0]?.querySelector('.compare-label')?.textContent.replace(/\s+/g, ' ').trim();
              const rowHeader = document.createElement('span');
              rowHeader.className = 'visually-hidden';
              rowHeader.id = `compare-row-heading-${key.replace(/[^a-z0-9_-]/gi, '-')}`;
              rowHeader.setAttribute('role', 'rowheader');
              rowHeader.textContent = visibleLabel || rowLabels[key] || 'Produktinformation';
              compareRow.appendChild(rowHeader);

              value.forEach((elem, index) => {
                const columnHeaderId = `compare-product-heading-${index}`;
                if (key === 'compare-title') {
                  elem.setAttribute('role', 'columnheader');
                  elem.id = columnHeaderId;
                } else {
                  elem.setAttribute('role', 'cell');
                  elem.setAttribute('aria-labelledby', `${rowHeader.id} ${columnHeaderId}`);
                }
                compareRow.appendChild(elem);
              });

              if (isHairOneFact) {
                const normalizedValues = value.map((elem) => (
                  elem.querySelector('.compare-value')?.textContent || ''
                ).replace(/\s+/g, ' ').trim());
                const distinctValues = new Set(normalizedValues.filter(Boolean));
                compareRow.classList.add(distinctValues.size > 1 ? 'compare-row--different' : 'compare-row--same');
              }
              if (isRequiredHairWaxFinish) compareRow.classList.add('compare-row--required');

              compareElem.appendChild(compareRow);
            }
          });

          this.compareArea.innerHTML = compareElem.innerHTML;
        } else {
          this.compareArea.removeAttribute('role');
          this.compareArea.removeAttribute('aria-label');
          this.compareArea.innerHTML = 'Für den Vergleich sind mindestens zwei verfügbare Produkte nötig.';
        }

        // Timeout to trigger CSS transition
        setTimeout(() => {
          this.loadingSpinner.setAttribute('hidden', 'true');
          this.loadingSpinner.setAttribute('aria-hidden', 'true');
          this.compareArea.classList.add('revealable--in');
          requestAnimationFrame(() => {
            if (this.detailsEl.open) trapFocus(this.detailsEl, this.closeButton[0]);
          });
        }, 100);
      }
    }

    /**
     * Fetches an array of html for each of the compare products, using the section rendering API
     * @param {Array} compareProducts - An array of product objects to compare
     */
    static async getCompareProducts(compareProducts) {
      const settled = await Promise.allSettled(compareProducts.map(async (product) => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), COMPARE_FETCH_TIMEOUT);
        try {
          const response = await fetch(`${product.url}?sections=product-compare`, {
            credentials: 'same-origin',
            signal: controller.signal
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const sections = await response.json();
          const sectionHtml = sections['product-compare'];
          if (typeof sectionHtml !== 'string') throw new Error('Missing compare section');
          const tmpl = document.createElement('template');
          tmpl.innerHTML = sectionHtml;
          const compareEl = tmpl.content.querySelector('.cc-compare');
          if (!compareEl) throw new Error('Missing compare markup');

          const templateEl = compareEl.querySelector('template');
          if (!templateEl) throw new Error('Missing compare template');
          const templateContent = document.importNode(templateEl.content, true);
          templateEl.parentNode.insertBefore(templateContent, templateEl);
          templateEl.remove();
          const renderedGroup = compareEl.querySelector('[data-compare-product-group]')?.dataset.compareProductGroup;
          if (!renderedGroup || renderedGroup !== product.group) throw new Error('Compare group mismatch');
          const renderedId = compareEl.querySelector('[data-compare-product-id]')?.dataset.compareProductId;
          if (!renderedId || renderedId !== product.id) throw new Error('Compare product mismatch');
          if (!compareEl.querySelector('[data-compare-key="compare-title"]')) throw new Error('Incomplete compare section');
          return { product, html: compareEl.outerHTML };
        } finally {
          clearTimeout(timeout);
        }
      }));

      const items = [];
      const validProducts = [];
      const failedProducts = [];
      settled.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          items.push(result.value);
          validProducts.push(result.value.product);
        } else {
          failedProducts.push(compareProducts[index]);
        }
      });
      return { items, validProducts, failedProducts };
    }
  }

  customElements.define('compare-modal', CompareModal);
});
